// ResponsiveBracketVisualizer.tsx - Updated without gear icon and with round indices
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Region, Team, BracketGame, WinnerSource, WinnerWithMetadata } from '../types/types';
import teamsData from '../data/teams.json';
import { useConfig } from '../context/ConfigContext';

// Import modular components
import BracketAccordion from './BracketAccordion';
import BracketGameComponent from './BracketGame';
import ChampionshipDisplay from './ChampionshipDisplay';
import PickBreakdownPanel from './PickBreakdownPanel';
import RegionBracket from './RegionBracket';
import TournamentBracketDesktop from './TournamentBracketDesktop';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger } from './ui/tabs';
import { Trash2, Save, Share2 } from 'lucide-react';

// Import utility functions
import {
  getTeamSeed,
  isTeamFromKnownWinnerPath,
  isTeamFromOfficialKnownWinnerPath,
  findSourceGameForTeam,
  initializeBracket,
} from '../utils/bracketUtils';
import { computePickBreakdowns, GameBreakdown } from '../utils/pickBreakdownUtils';

type BracketTab = 'scenario' | 'breakdown';

interface ResponsiveBracketVisualizerProps {
  winnersWithMetadata: WinnerWithMetadata[];
  onUserBracketChanges: (winners: WinnerWithMetadata[]) => void;
  onClearBracket?: () => void;
  onSaveScenario?: () => void;
  onShareScenario?: () => void;
  hasUnsavedChanges?: boolean;
  scenarioSelectorRef?: (el: HTMLDivElement | null) => void;
  playerPicks?: string;
  pinnedPlayers?: string[];
  onPinnedPlayersChange?: (pinnedPlayers: string[]) => void;
  /** Controlled tab state from parent (desktop). Falls back to internal state if omitted. */
  activeTab?: BracketTab;
  onTabChange?: (tab: BracketTab) => void;
  /** Controlled breakdown game selection from parent (desktop sidebar). */
  selectedBreakdownGame?: number | null;
  onBreakdownGameSelect?: (gameId: number | null) => void;
  /** Pre-computed breakdown map from parent to avoid duplicate computation. */
  breakdownMap?: Map<number, GameBreakdown>;
}

// Get teams from the generated JSON file or use fallback
let initialTeams: Team[] = [];

try {
  initialTeams = teamsData as Team[];
  if (!Array.isArray(initialTeams) || initialTeams.length === 0) {
    throw new Error('Invalid teams data');
  }
} catch (error) {
  console.warn('Error loading teams data, using fallback approach:', error);
  // Fallback implementation
  const regions = [Region.SOUTH, Region.EAST, Region.WEST, Region.MIDWEST];
  const seedOrder = [1, 16, 8, 9, 5, 12, 4, 13, 6, 11, 3, 14, 7, 10, 2, 15];

  regions.forEach(region => {
    const regionPrefix = region.charAt(0).toLowerCase();
    seedOrder.forEach(seed => {
      initialTeams.push({
        id: `${regionPrefix}${seed}`,
        name: `${region} Seed ${seed}`,
        seed: seed,
        region: region
      });
    });
  });
}

const ResponsiveBracketVisualizer: React.FC<ResponsiveBracketVisualizerProps> = ({
  winnersWithMetadata,
  onUserBracketChanges,
  onClearBracket,
  onSaveScenario,
  onShareScenario,
  hasUnsavedChanges,
  scenarioSelectorRef,
  playerPicks,
  pinnedPlayers = [],
  onPinnedPlayersChange,
  activeTab: controlledTab,
  onTabChange,
  selectedBreakdownGame: controlledSelectedGame,
  onBreakdownGameSelect,
  breakdownMap: controlledBreakdownMap,
}) => {
  // Get configuration from context
  const { roundConfigs } = useConfig();

  // Internal state (used when not controlled by parent, i.e. mobile)
  const [internalTab, setInternalTab] = useState<BracketTab>('scenario');
  const [internalSelectedGame, setInternalSelectedGame] = useState<number | null>(null);

  // Use controlled or internal state
  const activeTab = controlledTab ?? internalTab;
  const setActiveTab = controlledTab !== undefined
    ? (onTabChange ?? setInternalTab)
    : (tab: BracketTab) => { setInternalTab(tab); onTabChange?.(tab); };
  const selectedBreakdownGame = controlledSelectedGame !== undefined ? controlledSelectedGame : internalSelectedGame;
  const setSelectedBreakdownGame = onBreakdownGameSelect ?? setInternalSelectedGame;

  // Clear selected game when switching tabs (internal state only)
  useEffect(() => {
    if (!onBreakdownGameSelect) {
      setInternalSelectedGame(null);
    }
  }, [internalTab, onBreakdownGameSelect]);

  // Compute pick breakdowns locally only when not provided by parent
  const localBreakdownMap = useMemo(() => {
    if (controlledBreakdownMap || !playerPicks) return new Map<number, GameBreakdown>();
    const officialWinners = winnersWithMetadata.map(w =>
      w && w.source === WinnerSource.OFFICIAL ? w.team : ''
    );
    const breakdowns = computePickBreakdowns(playerPicks, officialWinners);
    const map = new Map<number, GameBreakdown>();
    for (const b of breakdowns) map.set(b.gameId, b);
    return map;
  }, [controlledBreakdownMap, playerPicks, winnersWithMetadata]);

  const breakdownMap = controlledBreakdownMap ?? localBreakdownMap;

  // Extract round names for bracket initialization
  const configuredRoundNames = [
    "Round of 64",
    ...roundConfigs.map(config => config.name)
  ];

  // Initialize games with configurable round names
  const [games, setGames] = useState<BracketGame[]>(
    initializeBracket(initialTeams, configuredRoundNames)
  );

  const [hoveredTeamId, setHoveredTeamId] = useState<string | null>(null);

  // Track expanded accordion sections by region (mobile)
  const [expandedRegions, setExpandedRegions] = useState<Record<string, boolean>>({});

  // Helper function to create winners with metadata from games
  const createWinnersWithMetadataFromGames = useCallback((gamesArray: BracketGame[]): WinnerWithMetadata[] => {
    const result: WinnerWithMetadata[] = [];

    gamesArray.forEach(game => {
      if (game.winner) {
        const isOfficial = game.id < winnersWithMetadata.length &&
                          winnersWithMetadata[game.id] &&
                          winnersWithMetadata[game.id].source === WinnerSource.OFFICIAL &&
                          winnersWithMetadata[game.id].team === game.winner;

        result[game.id] = {
          team: game.winner,
          source: isOfficial ? WinnerSource.OFFICIAL : WinnerSource.USER
        };
      }
    });

    return result;
  }, [winnersWithMetadata]);

  // Helper function to notify parent of changes
  const notifyParentOfChanges = useCallback((updatedGames: BracketGame[]) => {
    const updatedWinnersWithMetadata = createWinnersWithMetadataFromGames(updatedGames);
    onUserBracketChanges(updatedWinnersWithMetadata);
  }, [createWinnersWithMetadataFromGames, onUserBracketChanges]);

  // Auto-expand/collapse regions based on completion
  useEffect(() => {
    if (games.length === 0) return;

    const regionOrder = ['East', 'South', 'West', 'Midwest'];
    const newExpandedState: Record<string, boolean> = {};

    for (const region of regionOrder) {
      const regionGames = games.filter(g => String(g.region) === region && g.round < 4);
      const allDecided = regionGames.length > 0 && regionGames.every(game => {
        const metadata = game.id < winnersWithMetadata.length ? winnersWithMetadata[game.id] : null;
        return metadata && metadata.team === game.winner;
      });

      newExpandedState[region] = !allDecided;
    }

    setExpandedRegions(newExpandedState);
  }, [games, winnersWithMetadata]);

  // Apply winners from metadata when they change
  useEffect(() => {
    const newGames = initializeBracket(initialTeams, configuredRoundNames);

    if (!winnersWithMetadata || winnersWithMetadata.length === 0) {
      setGames(newGames);
      return;
    }

    // Apply winners from metadata and propagate through bracket
    for (let i = 0; i < winnersWithMetadata.length; i++) {
      const winner = winnersWithMetadata[i];
      if (winner && winner.team) {
        const game = newGames.find(g => g.id === i);
        if (game) {
          game.winner = winner.team;

          if (game.nextGameId !== undefined) {
            const nextGame = newGames.find(g => g.id === game.nextGameId);
            if (nextGame) {
              const isFirstPositionGame = (game.id % 2 === 0);
              if (isFirstPositionGame) {
                nextGame.teamA = winner.team;
              } else {
                nextGame.teamB = winner.team;
              }
            }
          }
        }
      }
    }

    // Propagate winners through bracket rounds
    for (let round = 0; round < 5; round++) {
      const gamesInRound = newGames.filter(g => g.round === round);

      for (const game of gamesInRound) {
        if (game.winner && game.nextGameId !== undefined) {
          const nextGame = newGames.find(g => g.id === game.nextGameId);
          if (nextGame) {
            if (nextGame.id < winnersWithMetadata.length && winnersWithMetadata[nextGame.id]) {
              continue;
            }

            const isFirstPosition = (game.id % 2 === 0);
            if (isFirstPosition) {
              nextGame.teamA = game.winner;
            } else {
              nextGame.teamB = game.winner;
            }
          }
        }
      }
    }

    setGames(newGames);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winnersWithMetadata]);

  // Function to recursively clear a team from future rounds
  const cascadeTeamRemoval = (gamesArray: BracketGame[], fromGameId: number, teamToRemove: string | undefined) => {
    if (!teamToRemove) return;

    const game = gamesArray.find(g => g.id === fromGameId);
    if (!game || game.nextGameId === undefined) return;

    const nextGame = gamesArray.find(g => g.id === game.nextGameId);
    if (!nextGame) return;

    const nextGameHasOfficialWinner = nextGame.id < winnersWithMetadata.length &&
                                  winnersWithMetadata[nextGame.id] &&
                                  winnersWithMetadata[nextGame.id].source === WinnerSource.OFFICIAL;

    if (nextGameHasOfficialWinner) return;

    const feedsIntoPositionA = (fromGameId % 2 === 0);

    if (feedsIntoPositionA && nextGame.teamA === teamToRemove) {
      nextGame.teamA = undefined;
    } else if (!feedsIntoPositionA && nextGame.teamB === teamToRemove) {
      nextGame.teamB = undefined;
    }

    if (nextGame.teamA === teamToRemove) {
      nextGame.teamA = undefined;
    }
    if (nextGame.teamB === teamToRemove) {
      nextGame.teamB = undefined;
    }

    if (nextGame.winner === teamToRemove) {
      nextGame.winner = undefined;
    }

    if (nextGame.nextGameId !== undefined) {
      cascadeTeamRemoval(gamesArray, nextGame.id, teamToRemove);
    }
  };

  // Game selection handler
  const handleTeamSelect = (gameId: number, team: string) => {
    const hasOfficialWinner = gameId < winnersWithMetadata.length &&
                          winnersWithMetadata[gameId] &&
                          winnersWithMetadata[gameId].source === WinnerSource.OFFICIAL;

    if (hasOfficialWinner) return;

    const updatedGames = [...games];
    const currentGame = updatedGames.find(g => g.id === gameId);
    if (!currentGame) return;

    // Handle winner replacement and cascading logic
    if (currentGame.winner && currentGame.winner !== team) {
      if (currentGame.nextGameId !== undefined) {
        cascadeTeamRemoval(updatedGames, currentGame.id, currentGame.winner);
      }
    }

    currentGame.winner = team;

    // Propagate to next game
    if (currentGame.nextGameId !== undefined) {
      const nextGame = updatedGames.find(g => g.id === currentGame.nextGameId);
      if (nextGame) {
        const nextGameHasOfficialWinner = nextGame.id < winnersWithMetadata.length &&
                                      winnersWithMetadata[nextGame.id] &&
                                      winnersWithMetadata[nextGame.id].source === WinnerSource.OFFICIAL;

        if (!nextGameHasOfficialWinner) {
          const isFirstPositionGame = (gameId % 2 === 0);
          const previousTeam = isFirstPositionGame ? nextGame.teamA : nextGame.teamB;

          if (previousTeam && previousTeam !== team) {
            cascadeTeamRemoval(updatedGames, nextGame.id, previousTeam);
          }

          if (isFirstPositionGame) {
            nextGame.teamA = team;
          } else {
            nextGame.teamB = team;
          }

          if (nextGame.winner &&
            (nextGame.winner !== nextGame.teamA && nextGame.winner !== nextGame.teamB)) {
            nextGame.winner = undefined;
          }
        }
      }
    }

    setGames(updatedGames);
    notifyParentOfChanges(updatedGames);
  };

  // Reset selection for a specific team in a game
  const handleResetTeam = (gameId: number, teamToReset: string | undefined) => {
    const hasOfficialWinner = gameId < winnersWithMetadata.length &&
                           winnersWithMetadata[gameId] &&
                           winnersWithMetadata[gameId].source === WinnerSource.OFFICIAL;

    if (hasOfficialWinner || !teamToReset) return;

    const updatedGames = [...games];
    const currentGame = updatedGames.find(g => g.id === gameId);
    if (!currentGame) return;

    const sourceGameId = findSourceGameForTeam(teamToReset, gameId, updatedGames);
    if (sourceGameId !== null) {
      const sourceGameHasOfficialWinner = sourceGameId < winnersWithMetadata.length &&
                                      winnersWithMetadata[sourceGameId] &&
                                      winnersWithMetadata[sourceGameId].source === WinnerSource.OFFICIAL;

      if (sourceGameHasOfficialWinner) return;

      const sourceGame = updatedGames.find(g => g.id === sourceGameId);
      if (sourceGame && sourceGame.winner === teamToReset) {
        sourceGame.winner = undefined;
      }
    }

    const isTeamA = currentGame.teamA === teamToReset;

    if (isTeamA) {
      currentGame.teamA = undefined;
    } else {
      currentGame.teamB = undefined;
    }

    if (currentGame.winner === teamToReset) {
      currentGame.winner = undefined;

      if (currentGame.nextGameId !== undefined) {
        cascadeTeamRemoval(updatedGames, currentGame.id, teamToReset);
      }
    }

    setGames(updatedGames);
    notifyParentOfChanges(updatedGames);
  };

  // Handle region accordion toggle
  const handleRegionToggle = (region: string, isOpen: boolean) => {
    setExpandedRegions(prev => ({
      ...prev,
      [region]: isOpen
    }));
  };

  // Compact render function for bracket layout
  const renderCompactGameComponent = (game: BracketGame) => (
    <BracketGameComponent
      key={game.id}
      game={game}
      compact
      knownWinnersWithMetadata={winnersWithMetadata}
      hoveredTeamId={hoveredTeamId}
      handleTeamSelect={handleTeamSelect}
      handleResetTeam={handleResetTeam}
      setHoveredTeamId={setHoveredTeamId}
      getTeamSeed={(teamName) => getTeamSeed(initialTeams, teamName)}
      findSourceGameForTeam={(teamName, gameId) => findSourceGameForTeam(teamName, gameId, games)}
      isTeamFromKnownWinnerPath={(teamName: string, gameId: number, winners: WinnerWithMetadata[], gamesArray: BracketGame[]) =>
        isTeamFromKnownWinnerPath(teamName, gameId, winners, gamesArray)}
      isTeamFromOfficialKnownWinnerPath={(teamName: string, gameId: number, winners: WinnerWithMetadata[], gamesArray: BracketGame[]) =>
        isTeamFromOfficialKnownWinnerPath(teamName, gameId, winners, gamesArray)}
      games={games}
    />
  );

  // Larger render function for championship game (+2pt)
  const renderChampionshipGameComponent = (game: BracketGame) => (
    <BracketGameComponent
      key={game.id}
      game={game}
      compact
      compactTextClass="text-[22px]"
      knownWinnersWithMetadata={winnersWithMetadata}
      hoveredTeamId={hoveredTeamId}
      handleTeamSelect={handleTeamSelect}
      handleResetTeam={handleResetTeam}
      setHoveredTeamId={setHoveredTeamId}
      getTeamSeed={(teamName) => getTeamSeed(initialTeams, teamName)}
      findSourceGameForTeam={(teamName, gameId) => findSourceGameForTeam(teamName, gameId, games)}
      isTeamFromKnownWinnerPath={(teamName: string, gameId: number, winners: WinnerWithMetadata[], gamesArray: BracketGame[]) =>
        isTeamFromKnownWinnerPath(teamName, gameId, winners, gamesArray)}
      isTeamFromOfficialKnownWinnerPath={(teamName: string, gameId: number, winners: WinnerWithMetadata[], gamesArray: BracketGame[]) =>
        isTeamFromOfficialKnownWinnerPath(teamName, gameId, winners, gamesArray)}
      games={games}
    />
  );

  // Breakdown render functions
  const renderBreakdownGameComponent = (game: BracketGame) => (
    <BracketGameComponent
      key={game.id}
      game={game}
      compact
      mode="breakdown"
      breakdown={breakdownMap.get(game.id)}
      onBreakdownClick={(gId) => setSelectedBreakdownGame(gId)}
      isSelectedForBreakdown={selectedBreakdownGame === game.id}
      knownWinnersWithMetadata={winnersWithMetadata}
      hoveredTeamId={null}
      handleTeamSelect={() => {}}
      handleResetTeam={() => {}}
      setHoveredTeamId={() => {}}
      getTeamSeed={(teamName) => getTeamSeed(initialTeams, teamName)}
      findSourceGameForTeam={() => null}
      isTeamFromKnownWinnerPath={() => false}
      isTeamFromOfficialKnownWinnerPath={() => false}
      games={games}
    />
  );

  const renderBreakdownChampionshipComponent = (game: BracketGame) => (
    <BracketGameComponent
      key={game.id}
      game={game}
      compact
      compactTextClass="text-[22px]"
      mode="breakdown"
      breakdown={breakdownMap.get(game.id)}
      onBreakdownClick={(gId) => setSelectedBreakdownGame(gId)}
      isSelectedForBreakdown={selectedBreakdownGame === game.id}
      knownWinnersWithMetadata={winnersWithMetadata}
      hoveredTeamId={null}
      handleTeamSelect={() => {}}
      handleResetTeam={() => {}}
      setHoveredTeamId={() => {}}
      getTeamSeed={(teamName) => getTeamSeed(initialTeams, teamName)}
      findSourceGameForTeam={() => null}
      isTeamFromKnownWinnerPath={() => false}
      isTeamFromOfficialKnownWinnerPath={() => false}
      games={games}
    />
  );

  // Region order for mobile accordion layout
  const regionOrder = ['East', 'South', 'West', 'Midwest'];

  // Choose render functions based on active tab
  const activeRenderGame = activeTab === 'breakdown' ? renderBreakdownGameComponent : renderCompactGameComponent;
  const activeRenderChampionship = activeTab === 'breakdown' ? renderBreakdownChampionshipComponent : renderChampionshipGameComponent;

  // Selected game data for breakdown panel
  const selectedBreakdown = selectedBreakdownGame !== null ? breakdownMap.get(selectedBreakdownGame) ?? null : null;

  // Champion breakdown for mobile
  const champBreakdown = breakdownMap.get(62);

  return (
    <div className="my-5 md:my-5">
      <div className="flex flex-col gap-2 mb-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:gap-3">
          {playerPicks ? (
            <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'scenario' | 'breakdown')}>
              <TabsList>
                <TabsTrigger value="scenario" className="text-sm font-semibold">
                  Scenario Builder
                </TabsTrigger>
                <TabsTrigger value="breakdown" className="text-sm font-semibold">
                  Pick Breakdown
                </TabsTrigger>
              </TabsList>
            </Tabs>
          ) : (
            <h2 className="text-xl font-bold text-foreground md:text-2xl">Scenario Builder</h2>
          )}
          {/* Scenario selector dropdown portal target - only in scenario mode */}
          {activeTab === 'scenario' && <div ref={scenarioSelectorRef} />}
        </div>
        {activeTab === 'scenario' && (
          <div className="flex items-center gap-2">
            {hasUnsavedChanges && onShareScenario && (
              <Button
                variant="default"
                size="sm"
                onClick={onShareScenario}
              >
                <Share2 className="h-3.5 w-3.5 mr-1" />
                Share
              </Button>
            )}
            {hasUnsavedChanges && onSaveScenario && (
              <Button
                variant="default"
                size="sm"
                onClick={onSaveScenario}
              >
                <Save className="h-3.5 w-3.5 mr-1" />
                Save
              </Button>
            )}
            {onClearBracket && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearBracket}
                className="text-muted-foreground hover:text-muted-foreground lg:hover:text-destructive"
              >
                <Trash2 className="h-3.5 w-3.5 mr-1" />
                Clear Bracket
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Desktop: region-first bracket layout (lg and above) */}
      <div className="hidden lg:block">
        <TournamentBracketDesktop
          games={games}
          winnersWithMetadata={winnersWithMetadata}
          renderCompactGame={activeRenderGame}
          renderChampionshipGame={activeRenderChampionship}
          setGames={setGames}
          onBracketChange={notifyParentOfChanges}
          championBreakdown={activeTab === 'breakdown' ? champBreakdown : undefined}
        />
      </div>

      {/* Mobile/Tablet: region-first accordion layout (below lg) */}
      <div className="lg:hidden">
        {/* Render each region as an accordion with a horizontally-scrolling mini-bracket */}
        {regionOrder.map((region, regionIndex) => {
          const isExpanded = expandedRegions[region] !== false;

          return (
            <BracketAccordion
              key={`region-${region}`}
              title={`${region} Region`}
              roundIndex={regionIndex}
              defaultOpen={isExpanded}
              onToggle={(open) => handleRegionToggle(region, open)}
              showEditButton={false}
            >
              <div className="overflow-x-auto -mx-4 px-4">
                <div className="min-w-[500px]">
                  <RegionBracket
                    region={region}
                    games={games}
                    direction="ltr"
                    renderGame={activeRenderGame}
                    showRoundLabels={true}
                  />
                </div>
              </div>
            </BracketAccordion>
          );
        })}

        {/* Final Four and Championship display - mobile only */}
        {activeTab === 'breakdown' ? (
          <div className="mt-5 space-y-4">
            <h3 className="text-lg font-bold text-center text-primary">Final Four</h3>
            <div className="flex gap-4 justify-center">
              {games.filter(g => g.round === 4).sort((a, b) => a.id - b.id).map(g => (
                <div key={g.id} className="flex-1 max-w-[250px]">
                  {renderBreakdownGameComponent(g)}
                </div>
              ))}
            </div>
            <h3 className="text-lg font-bold text-center text-primary">Championship</h3>
            <div className="max-w-[250px] mx-auto">
              {games.filter(g => g.round === 5).map(g => (
                <div key={g.id}>{renderBreakdownGameComponent(g)}</div>
              ))}
            </div>
          </div>
        ) : (
          <ChampionshipDisplay
            games={games}
            knownWinners={winnersWithMetadata}
            setGames={setGames}
            hoveredTeamId={hoveredTeamId}
            handleTeamSelect={handleTeamSelect}
            handleResetTeam={handleResetTeam}
            setHoveredTeamId={setHoveredTeamId}
            getTeamSeed={(teamName) => getTeamSeed(initialTeams, teamName)}
            findSourceGameForTeam={(teamName, gameId) => findSourceGameForTeam(teamName, gameId, games)}
            isTeamFromKnownWinnerPath={(teamName: string, gameId: number, winners: WinnerWithMetadata[], gamesArray: BracketGame[]) =>
              isTeamFromKnownWinnerPath(teamName, gameId, winners, gamesArray)}
            isTeamFromOfficialKnownWinnerPath={(teamName: string, gameId: number, winners: WinnerWithMetadata[], gamesArray: BracketGame[]) =>
              isTeamFromOfficialKnownWinnerPath(teamName, gameId, winners, gamesArray)}
            onBracketChange={notifyParentOfChanges}
          />
        )}
      </div>

      {/* Pick Breakdown Panel — mobile only (desktop uses sidebar) */}
      <div className="lg:hidden">
        {activeTab === 'breakdown' && selectedBreakdown && (
          <PickBreakdownPanel
            key={selectedBreakdownGame}
            breakdown={selectedBreakdown}
            pinnedPlayers={pinnedPlayers}
            onPinnedPlayersChange={onPinnedPlayersChange}
            getTeamSeed={(teamName) => getTeamSeed(initialTeams, teamName)}
            onClose={() => setSelectedBreakdownGame(null)}
            mobile
          />
        )}
      </div>
    </div>
  );
};

export default ResponsiveBracketVisualizer;
